// Helpers for interactive drawing answers that also ask for a typed answer
// (spec.written = { prompt, answer, require_drawing }). When a spec has `written`, the answer value is
// stored as { interactive: [...placements], text: "..." } instead of a bare placement array.
// Shared by StudentPaperTake and StudentMockExamTake.

export const interactiveWrittenOf = (spec) =>
  spec && spec.written && (spec.written.prompt || spec.written.answer) ? spec.written : null;

// cut-drag answers are an object { cut: [[x,y],[x,y]], offsets: [[dx,dy],[dx,dy]] } — passed through
// whole (the backend grades the object). Other kinds store a bare placement array (or the written
// wrapper around one). cut-drag with a written companion is not supported.
export const interactiveArrayOf = (v) => {
  if (Array.isArray(v)) return v;
  if (v && Array.isArray(v.cut)) return v;
  // reflect/rotate/closed-polygon edge shapes: { points:[[x,y]…], edges:["gx,gy|gx,gy"…] } — the
  // placements are the points; the graded outline travels alongside on the value object.
  if (v && Array.isArray(v.points)) return v.points;
  if (v && Array.isArray(v.interactive)) return v.interactive;
  // edges-only ("join the printed points", no new points placed): the edges are the whole answer, so
  // treat their presence like a non-empty placement array rather than reporting length 0.
  if (v && !Array.isArray(v) && Array.isArray(v.edges) && v.edges.length > 0) return v.edges;
  return [];
};

// The value to hand to the canvas and to SUBMIT — preserves the whole shape so graded extras (the
// reflect/rotate/closed-polygon outline in { points, edges }, a cut-drag object, or a shape_label) survive
// round-trip. Use this instead of interactiveArrayOf wherever the value is fed back into the canvas or
// sent to the server; interactiveArrayOf stays for length/content checks that only care about placements.
export const interactiveValueOf = (v) => {
  if (v && !Array.isArray(v) && Array.isArray(v.points)) return v; // { points, edges?, shape_label? }
  if (v && !Array.isArray(v) && Array.isArray(v.cut)) return v;    // cut-drag object
  if (v && !Array.isArray(v) && typeof v.shape_label === "string") return v;
  // edges-only, no plotted points: preserve the whole { edges } object so the graded outline survives —
  // interactiveArrayOf's edges array alone would lose the "these are edges, not points" shape.
  if (v && !Array.isArray(v) && Array.isArray(v.edges) && v.edges.length > 0) return v;
  return interactiveArrayOf(v);
};

export const interactiveShapeLabelOf = (v) =>
  v && !Array.isArray(v) && typeof v.shape_label === "string" ? v.shape_label.trim() : "";

export const interactiveHasContent = (v) => {
  const a = interactiveArrayOf(v);
  if (Array.isArray(a) && a.length > 0) return true;
  // Edges-only questions ("join the three printed corners"): no_new_points means the student never
  // places a point, so the drawn edges are the whole answer. Without this the answer counts as empty —
  // the palette shows the part unanswered and the submit path drops the row before it is graded.
  if (v && !Array.isArray(v) && Array.isArray(v.edges) && v.edges.length > 0) return true;
  if (interactiveShapeLabelOf(v)) return true;
  return !!(a && Array.isArray(a.cut) && a.cut.length >= 2);
};

export const interactiveWrittenText = (v) =>
  v && !Array.isArray(v) && typeof v.text === "string" ? v.text : "";
